import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";

/**
 * SignaturePad — canvas-based e-signature capture used by both the dealer
 * and agent producer-agreement flows. Emits a PNG data URL to `onChange`
 * on each stroke completion; `null` when cleared.
 */
export default function SignaturePad({ onChange, testId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0B132B";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const isTouch = e.touches && e.touches[0];
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange && onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange && onChange(null);
  };

  return (
    <div className="border-2 border-dashed border-[#0B132B]/30 rounded-sm bg-white" data-testid={testId}>
      <canvas
        ref={canvasRef}
        width={560}
        height={180}
        className="w-full block touch-none cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <div className="flex items-center justify-between px-3 py-2 border-t border-[#E9ECEF] bg-[#F8F9FA]">
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D]">
          {empty ? "Draw your signature above" : "Signed"}
        </span>
        <button
          type="button"
          onClick={clear}
          className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] hover:text-red-600 inline-flex items-center gap-1"
          data-testid={`${testId}-clear`}
        >
          <Trash2 size={11} /> Clear
        </button>
      </div>
    </div>
  );
}
